import React from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from '../components/PageTitle';
import {
  BackLink,
  LegalBody,
  LegalContainer,
  LegalHeader,
  LegalMeta,
  LegalPage,
  LegalSection,
} from '../components/legal/LegalLayout';

const LAST_UPDATED = '8 July 2026';

const PRIVACY_SECTION_ORDER = [
  'collect',
  'use',
  'ai',
  'cookies',
  'localStorage',
  'sharing',
  'retention',
  'security',
  'rights',
  'children',
  'transfers',
  'changes',
  'contact',
] as const;

const PrivacyPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <LegalPage>
      <LegalContainer>
        <LegalHeader>
          <BackLink to="/">{t('legal.backToTemplates')}</BackLink>
          <PageTitle>{t('legal.privacy.title')}</PageTitle>
          <LegalMeta>{t('legal.lastUpdated', { date: LAST_UPDATED })}</LegalMeta>
        </LegalHeader>

        <LegalBody>
          <LegalSection>
            <p>{t('legal.privacy.intro')}</p>
          </LegalSection>

          {PRIVACY_SECTION_ORDER.map((sectionKey) => {
            if (sectionKey === 'collect') {
              const items = t(`legal.privacy.sections.${sectionKey}.items`, {
                returnObjects: true,
              }) as Record<string, string>;
              return (
                <LegalSection key={sectionKey}>
                  <h2>{t(`legal.privacy.sections.${sectionKey}.title`)}</h2>
                  <p>{t(`legal.privacy.sections.${sectionKey}.intro`)}</p>
                  <ul>
                    {Object.entries(items).map(([key, value]) => (
                      <li key={key}>
                        <strong>{value.split(' — ')[0]}</strong>
                        {value.includes(' — ') ? ` — ${value.split(' — ').slice(1).join(' — ')}` : ''}
                      </li>
                    ))}
                  </ul>
                </LegalSection>
              );
            }

            if (sectionKey === 'use' || sectionKey === 'sharing') {
              const items = t(`legal.privacy.sections.${sectionKey}.items`, {
                returnObjects: true,
              }) as string[];
              return (
                <LegalSection key={sectionKey}>
                  <h2>{t(`legal.privacy.sections.${sectionKey}.title`)}</h2>
                  <p>{t(`legal.privacy.sections.${sectionKey}.intro`)}</p>
                  <ul>
                    {items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </LegalSection>
              );
            }

            if (sectionKey === 'cookies') {
              return (
                <LegalSection key={sectionKey}>
                  <h2>{t(`legal.privacy.sections.${sectionKey}.title`)}</h2>
                  <p>{t(`legal.privacy.sections.${sectionKey}.body1`)}</p>
                  <p>{t(`legal.privacy.sections.${sectionKey}.body2`)}</p>
                </LegalSection>
              );
            }

            if (sectionKey === 'contact') {
              return (
                <LegalSection key={sectionKey}>
                  <h2>{t(`legal.privacy.sections.${sectionKey}.title`)}</h2>
                  <p>
                    {t(`legal.privacy.sections.${sectionKey}.body`)}{' '}
                    <a href={`mailto:${t(`legal.privacy.sections.${sectionKey}.email`)}`}>
                      {t(`legal.privacy.sections.${sectionKey}.email`)}
                    </a>
                    .
                  </p>
                </LegalSection>
              );
            }

            return (
              <LegalSection key={sectionKey}>
                <h2>{t(`legal.privacy.sections.${sectionKey}.title`)}</h2>
                <p>{t(`legal.privacy.sections.${sectionKey}.body`)}</p>
              </LegalSection>
            );
          })}
        </LegalBody>
      </LegalContainer>
    </LegalPage>
  );
};

export default PrivacyPage;
